import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Link,
  Typography,
} from '@mui/material';
import axios from 'axios';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_ENDPOINTS from '../config/api';

import { useAuth } from '../contexts/AuthContext';

const FONT_FAMILY =
  'Poppins, Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif';

/**
 * ProviderConsentPage — re-acceptance gate for Provider Association T&Cs and
 * Privacy Policy. Shown to authenticated users whose active membership has
 * stale stored versions (``user.provider_terms_required === true``). Mirrors
 * the user-level ``ConsentPage``.
 */
const ProviderConsentPage: React.FC = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const termsVersion = user?.active_membership?.provider_terms_version ?? '';
  const privacyVersion = user?.active_membership?.provider_privacy_version ?? '';
  const canSubmit = termsAccepted && privacyAccepted && !saving && !!termsVersion;

  const handleSubmit = async (): Promise<void> => {
    try {
      setSaving(true);
      setError(null);
      await axios.post(API_ENDPOINTS.AUTH.PROVIDER_TERMS_ACCEPT, {
        accepted_terms_version: termsVersion,
        accepted_privacy_version: privacyVersion,
      });
      // Reason: re-fetch user so provider_terms_required flips to false and
      // the gate unlocks.
      await refreshUser();
      navigate('/home');
    } catch (err: any) {
      console.error('Failed to submit Provider terms:', err);
      setError(
        err.response?.data?.detail
          || 'Something went wrong. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container
      component="main"
      maxWidth="xs"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 6, sm: 10 },
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            fontSize: { xs: 32, sm: 38 },
            lineHeight: 1.3,
            mb: 2,
          }}
        >
          Provider Terms Updated
        </Typography>

        <Typography
          sx={{
            fontFamily: FONT_FAMILY,
            fontSize: 15,
            color: '#555',
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          The Provider Association Terms &amp; Conditions and Privacy Policy
          have been updated. Please review and accept them to continue using
          Provider features.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            bgcolor: '#F9F9F9',
            borderRadius: '16px',
            p: 2.5,
            mb: 3,
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={saving}
                sx={{ '&.Mui-checked': { color: '#FFD400' } }}
              />
            }
            label={
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                I accept the{' '}
                <Link
                  href="https://jeffsays.com/provider-terms.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#1976d2' }}
                >
                  Provider Terms &amp; Conditions
                </Link>
              </Typography>
            }
            sx={{ mb: 1.5, alignItems: 'center' }}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={privacyAccepted}
                onChange={(e) => setPrivacyAccepted(e.target.checked)}
                disabled={saving}
                sx={{ '&.Mui-checked': { color: '#FFD400' } }}
              />
            }
            label={
              <Typography sx={{ fontFamily: FONT_FAMILY, fontSize: 14 }}>
                I accept the{' '}
                <Link
                  href="https://jeffsays.com/provider-privacy.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: '#1976d2' }}
                >
                  Provider Privacy Policy
                </Link>
              </Typography>
            }
            sx={{ alignItems: 'center' }}
          />
        </Box>

        <Button
          variant="contained"
          fullWidth
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{
            bgcolor: '#FFD400',
            color: '#000000',
            fontFamily: FONT_FAMILY,
            fontWeight: 700,
            borderRadius: '12px',
            py: 1.75,
            textTransform: 'none',
            fontSize: 16,
            mb: 1.5,
            '&:hover': { bgcolor: '#E6BF00' },
            '&.Mui-disabled': {
              bgcolor: '#E0E0E0',
              color: '#999',
            },
          }}
        >
          {saving ? <CircularProgress size={24} /> : 'Continue'}
        </Button>

        <Button
          variant="outlined"
          fullWidth
          onClick={() => {
            logout();
            navigate('/login');
          }}
          disabled={saving}
          sx={{
            color: '#666',
            borderColor: '#CCC',
            fontFamily: FONT_FAMILY,
            fontWeight: 600,
            borderRadius: '12px',
            py: 1.5,
            textTransform: 'none',
            fontSize: 15,
            '&:hover': {
              borderColor: '#999',
              bgcolor: 'rgba(0, 0, 0, 0.03)',
            },
          }}
        >
          Sign Out
        </Button>
      </Box>
    </Container>
  );
};

export default ProviderConsentPage;
